import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  Clock3,
  MoreVertical,
  RefreshCw,
  Search,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { format, isToday, isTomorrow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { appointmentService, doctorService, getLocalizedValue } from '@/services/api';
import { parseApiDateTime } from '@/lib/dateTime';
import { cn } from '@/lib/utils';

const STATUS_PRESENTATION = {
  pending: {
    en: 'Pending',
    ar: 'قيد الانتظار',
    badgeClass: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
  },
  accepted: {
    en: 'Accepted',
    ar: 'مقبول',
    badgeClass: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200',
  },
  in_clinic: {
    en: 'In The Clinic',
    ar: 'داخل العيادة',
    badgeClass: 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-200',
  },
  done: {
    en: 'Done',
    ar: 'مكتمل',
    badgeClass: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200',
  },
  cancel: {
    en: 'Cancelled',
    ar: 'ملغي',
    badgeClass: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200',
  },
  waiting: {
    en: 'Waiting',
    ar: 'انتظار',
    badgeClass: 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-200',
  },
  unfinished: {
    en: 'Unfinished',
    ar: 'غير مكتمل',
    badgeClass: 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-200',
  },
  unknown: {
    en: 'Unknown',
    ar: 'غير معروف',
    badgeClass: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200',
  },
};

const STATUS_ORDER = [
  'waiting',
  'pending',
  'accepted',
  'confirmed',
  'in_clinic',
  'in_progress',
  'done',
  'completed',
  'cancel',
  'cancelled',
  'rejected',
  'no_show',
  'unfinished',
  'draft',
];

const DEFAULT_VISIBLE_STATUSES = [
  'pending',
  'accepted',
  'cancel',
  'in_clinic',
  'done',
  'unfinished',
];

const STATUS_BY_ID = {
  1: 'pending',
  2: 'accepted',
  3: 'in_clinic',
  4: 'done',
  5: 'cancel',
  6: 'cancel',
  7: 'waiting',
};

const STATUS_ID_BY_STATUS = {
  pending: 1,
  accepted: 2,
  in_clinic: 3,
  done: 4,
  cancel: 5,
  waiting: 7,
};

const NEXT_STATUS_BY_CURRENT = {
  pending: 'accepted',
  accepted: 'in_clinic',
  in_clinic: 'done',
};

const getAppointmentDateValue = (appointment) =>
  appointment?.appointment_at ||
  appointment?.appointment_date ||
  appointment?.start_at ||
  appointment?.date ||
  null;

const normalizeStatus = (state, statusId, appointmentType) => {
  const raw = String(state || STATUS_BY_ID[Number(statusId)] || appointmentType || 'pending')
    .trim()
    .toLowerCase();

  if (raw === 'waiting') return 'waiting';
  if (['accepted', 'confirmed'].includes(raw)) return 'accepted';
  if (['in_clinic', 'in_progress'].includes(raw)) return 'in_clinic';
  if (['done', 'completed'].includes(raw)) return 'done';
  if (['cancel', 'cancelled', 'canceled', 'rejected', 'no_show'].includes(raw)) return 'cancel';
  if (raw === 'unfinished') return 'unfinished';
  if (raw === 'draft') return 'draft';
  if (raw === 'pending') return 'pending';

  return raw || 'unknown';
};

const isWaitingAppointment = (appointment = {}) => {
  const type = String(appointment.appointment_type || appointment.type || '').trim().toLowerCase();
  const state = String(appointment.state || appointment.appointment_status?.status || '')
    .trim()
    .toLowerCase();

  return (
    appointment.is_waiting_appointment === true ||
    type === 'waiting' ||
    state === 'waiting' ||
    Number(appointment.appointment_status_id) === 7
  );
};

const shouldStayInWaitingSection = (appointment = {}) =>
  appointment.isWaiting && ['waiting', 'pending', 'accepted'].includes(appointment.status);

const isUnfinishedStatus = (status) => !['done', 'cancel'].includes(status);

const hasQueueInsights = (appointment = {}) =>
  appointment.showQueueNumberToPatient ||
  appointment.showRemainingTurnsToPatient ||
  appointment.showEstimatedTurnTimeToPatient ||
  appointment.queueDisplayLabel != null ||
  appointment.remainingTurnsCount != null ||
  appointment.estimatedWaitTime != null;

const getAppointmentModeLabel = (isWaiting, lang) =>
  isWaiting
    ? lang === 'ar'
      ? 'موعد انتظار'
      : 'Waiting Appointment'
    : lang === 'ar'
      ? 'موعد مجدول'
      : 'Scheduled Appointment';

const getStatusLabel = (status, lang) => {
  const config = STATUS_PRESENTATION[status];
  if (config) {
    return lang === 'ar' ? config.ar : config.en;
  }

  const prettified = String(status || 'unknown')
    .trim()
    .replaceAll('_', ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return prettified || (lang === 'ar' ? 'غير معروف' : 'Unknown');
};

const normalizeGender = (value, lang) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'male') return lang === 'ar' ? 'ذكر' : 'Male';
  if (normalized === 'female') return lang === 'ar' ? 'أنثى' : 'Female';
  return String(value || '').trim();
};

const getNextStatus = (status) => NEXT_STATUS_BY_CURRENT[status] || null;

const sortStatusKeys = (statuses) =>
  [...statuses].sort((left, right) => {
    const leftIndex = STATUS_ORDER.indexOf(left);
    const rightIndex = STATUS_ORDER.indexOf(right);

    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });

const DoctorAppointments = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState('pending');
  const [didResolveInitialStatus, setDidResolveInitialStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [doctorSettings, setDoctorSettings] = useState({
    acceptAppointment: false,
    enableWaitingAppointments: true,
  });

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentService.getAllAppointments();
      const rawData = response.data?.data || response.data || [];

      const transformedAppointments = rawData
        .map((apt) => {
          const rawDate = getAppointmentDateValue(apt);
          const date = parseApiDateTime(rawDate);
          const status = normalizeStatus(
            apt.state || apt.appointment_status?.status,
            apt.appointment_status_id,
            apt.appointment_type || apt.type
          );
          const patientName = apt.patient
            ? `${getLocalizedValue(apt.patient.first_name, lang) || ''} ${getLocalizedValue(apt.patient.last_name, lang) || ''}`.trim()
            : '';
          const isWaiting = isWaitingAppointment(apt);

          return {
            id: apt.id,
            patient: {
              id: apt.patient?.id || apt.patient_id,
              name: patientName || apt.patient_name || (lang === 'ar' ? 'مريض' : 'Patient'),
              phone: apt.patient?.phone_number || apt.patient?.mobile_number || apt.phone || '',
              image: apt.patient?.media?.[0]?.url || apt.patient?.media?.[0]?.thumb || '',
              age: apt.patient?.age ?? apt.patient_age,
              gender: apt.patient?.gender || apt.patient_gender || '',
            },
            doctor: {
              id: apt.doctor?.id || apt.doctor_id,
              name: getLocalizedValue(apt.doctor?.name, lang) || '',
            },
            date,
            time: date ? format(date, 'hh:mm a') : '--:--',
            type: apt.online
              ? lang === 'ar'
                ? 'استشارة أونلاين'
                : 'Online Consultation'
              : getLocalizedValue(apt.doctor_service?.name, lang) ||
                apt.service_name ||
                apt.doctor_service_name ||
                (lang === 'ar' ? 'زيارة عيادة' : 'Clinic Visit'),
            status,
            statusId: Number(apt.appointment_status_id || 1),
            notes: apt.notes || apt.hint || apt.chief_complaint || '',
            online: !!apt.online,
            clinic: getLocalizedValue(apt.clinic?.name, lang) || apt.clinic_name || '',
            isWaiting,
            queueDisplayLabel: apt.queue_display_label || '',
            remainingTurnsCount: apt.remaining_turns_count ?? apt.remaining_queue_turns,
            estimatedWaitTime: apt.estimated_wait_time ?? apt.estimated_turn_arrival_minutes,
            showQueueNumberToPatient: !!apt.show_queue_number_to_patient,
            showRemainingTurnsToPatient: !!apt.show_remaining_turns_to_patient,
            showEstimatedTurnTimeToPatient: !!apt.show_estimated_turn_time_to_patient,
            reference: apt.reference || apt.code || '',
            appointmentModeLabel: getAppointmentModeLabel(isWaiting, lang),
          };
        })
        .filter((apt) => apt.date instanceof Date && !Number.isNaN(apt.date.getTime()))
        .sort((a, b) => a.date - b.date);

      setAppointments(transformedAppointments);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل المواعيد' : 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAppointments();
  }, [fetchAppointments, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchDoctorSettings = async () => {
      try {
        const response = await doctorService.getProfile();
        const doctor = response.data?.data || response.data || {};

        setDoctorSettings({
          acceptAppointment: !!doctor.accept_appointment,
          enableWaitingAppointments: !!doctor.enable_waiting_appointments,
        });
      } catch (error) {
        console.error('Failed to fetch doctor settings:', error);
      }
    };

    fetchDoctorSettings();
  }, [isAuthenticated]);

  const filteredAppointments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return appointments.filter((appointment) => {
      const matchesSearch =
        !query ||
        appointment.patient.name.toLowerCase().includes(query) ||
        appointment.reference.toLowerCase().includes(query) ||
        appointment.type.toLowerCase().includes(query);

      const matchesDate = !selectedDate || format(appointment.date, 'yyyy-MM-dd') === selectedDate;

      return matchesSearch && matchesDate;
    });
  }, [appointments, searchQuery, selectedDate]);

  const scheduledAppointments = useMemo(
    () => filteredAppointments.filter((appointment) => !shouldStayInWaitingSection(appointment)),
    [filteredAppointments]
  );

  const waitingAppointments = useMemo(
    () => filteredAppointments.filter((appointment) => shouldStayInWaitingSection(appointment)),
    [filteredAppointments]
  );

  const nonWaitingAppointments = useMemo(
    () => filteredAppointments.filter((appointment) => !shouldStayInWaitingSection(appointment)),
    [filteredAppointments]
  );

  const visibleStatusFilters = useMemo(() => {
    const collectedStatuses = sortStatusKeys(
      new Set([
        ...DEFAULT_VISIBLE_STATUSES,
        ...appointments
          .map((appointment) => appointment.status)
          .filter((status) => status && status !== 'waiting' && status !== 'unknown'),
      ])
    );

    const filteredStatuses = doctorSettings.acceptAppointment
      ? collectedStatuses.filter((status) => status !== 'pending')
      : collectedStatuses;

    return [
      ...filteredStatuses.map((status) => ({
        key: status,
        en: getStatusLabel(status, 'en'),
        ar: getStatusLabel(status, 'ar'),
      })),
      { key: 'all', en: 'All', ar: 'الكل' },
    ];
  }, [appointments, doctorSettings.acceptAppointment]);

  useEffect(() => {
    const preferredStatus = doctorSettings.acceptAppointment ? 'accepted' : 'pending';
    const statusExists = visibleStatusFilters.some((filter) => filter.key === preferredStatus);
    const fallbackStatus = statusExists ? preferredStatus : 'all';

    if (!didResolveInitialStatus) {
      setActiveStatus(fallbackStatus);
      setDidResolveInitialStatus(true);
      return;
    }

    setActiveStatus((current) => {
      if (current === 'waiting_only' && !doctorSettings.enableWaitingAppointments) {
        return fallbackStatus;
      }

      if (!current) {
        return fallbackStatus;
      }

      if (visibleStatusFilters.some((filter) => filter.key === current)) {
        return current;
      }

      return fallbackStatus;
    });
  }, [
    didResolveInitialStatus,
    doctorSettings.acceptAppointment,
    doctorSettings.enableWaitingAppointments,
    visibleStatusFilters,
  ]);

  const filteredDisplayAppointments = useMemo(() => {
    if (activeStatus === 'all') return filteredAppointments;
    if (activeStatus === 'waiting_only') {
      return filteredAppointments.filter((appointment) => shouldStayInWaitingSection(appointment));
    }
    if (activeStatus === 'unfinished') {
      return nonWaitingAppointments.filter((appointment) => isUnfinishedStatus(appointment.status));
    }
    return nonWaitingAppointments.filter((appointment) => appointment.status === activeStatus);
  }, [activeStatus, filteredAppointments, nonWaitingAppointments]);

  const statusCounts = useMemo(
    () =>
      visibleStatusFilters.reduce((acc, filter) => {
        if (filter.key === 'all') {
          acc[filter.key] = filteredAppointments.length;
        } else if (filter.key === 'unfinished') {
          acc[filter.key] = nonWaitingAppointments.filter((appointment) =>
            isUnfinishedStatus(appointment.status)
          ).length;
        } else {
          acc[filter.key] = nonWaitingAppointments.filter(
            (appointment) => appointment.status === filter.key
          ).length;
        }
        return acc;
      }, {}),
    [filteredAppointments, nonWaitingAppointments, visibleStatusFilters]
  );

  const formatAppointmentDate = (date) => {
    if (isToday(date)) {
      return lang === 'ar' ? 'اليوم' : 'Today';
    }
    if (isTomorrow(date)) {
      return lang === 'ar' ? 'غدًا' : 'Tomorrow';
    }
    return format(date, 'EEE, MMM d, yyyy', { locale });
  };

  const getStatusBadge = (statusId, fallbackStatus) => {
    const canonicalStatus = normalizeStatus(fallbackStatus, statusId);
    const config = STATUS_PRESENTATION[canonicalStatus] || STATUS_PRESENTATION.unknown;

    return (
      <Badge
        className={cn(
          'rounded-full border px-4 py-1 text-sm font-semibold shadow-none',
          config.badgeClass
        )}
      >
        {lang === 'ar' ? config.ar : config.en}
      </Badge>
    );
  };

  const updateAppointmentLocally = (appointmentId, targetStatus, notes) => {
    setAppointments((current) =>
      current.map((appointment) =>
        appointment.id === appointmentId
          ? {
              ...appointment,
              status: targetStatus,
              statusId: STATUS_ID_BY_STATUS[targetStatus] ?? appointment.statusId,
              ...(notes !== undefined ? { notes } : {}),
            }
          : appointment
      )
    );
  };

  const syncAppointmentStatus = async (appointment, targetStatus) => {
    return appointmentService.updateStatus(appointment.id, targetStatus);
  };

  const openAppointmentDetails = (appointment) => {
    navigate(`/doctor/appointments/${appointment.id}`, {
      state: {
        from: '/doctor/appointments',
      },
    });
  };

  const canCancelAppointment = (appointment) =>
    ['pending', 'accepted'].includes(appointment.status);

  const handleAdvanceAppointmentStatus = async (appointment) => {
    const nextStatus = getNextStatus(appointment.status);
    if (!nextStatus) return;

    try {
      await syncAppointmentStatus(appointment, nextStatus);
      updateAppointmentLocally(appointment.id, nextStatus);
      toast.success(
        lang === 'ar'
          ? `تم تحديث حالة الموعد إلى ${getStatusLabel(nextStatus, 'ar')}`
          : `Appointment status updated to ${getStatusLabel(nextStatus, 'en')}`
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (lang === 'ar' ? 'فشل في تحديث حالة الموعد' : 'Failed to update appointment status');
      toast.error(message);
    }
  };

  const handleCancelAppointment = async (appointment) => {
    const confirmed = window.confirm(
      lang === 'ar'
        ? 'هل تريد إلغاء هذا الموعد؟'
        : 'Do you want to cancel this appointment?'
    );
    if (!confirmed) return;

    try {
      const response = await appointmentService.cancel(appointment.id);
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to cancel appointment');
      }

      setAppointments((current) =>
        current.map((item) =>
          item.id === appointment.id
            ? {
                ...item,
                status: 'cancel',
                statusId: 5,
              }
            : item
        )
      );

      toast.success(lang === 'ar' ? 'تم إلغاء الموعد' : 'Appointment cancelled');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (lang === 'ar' ? 'فشل في إلغاء الموعد' : 'Failed to cancel appointment');
      toast.error(message);
    }
  };

  const renderLoadingCards = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((item) => (
        <Card key={item} className="border-slate-700/70 bg-slate-800/80 shadow-xl shadow-slate-950/20">
          <CardContent className="p-5">
            <div className="flex flex-col gap-4 md:flex-row">
              <Skeleton className="h-40 w-full rounded-[28px] bg-slate-700/60 md:h-44 md:w-32" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-8 w-1/3 bg-slate-700/60" />
                <Skeleton className="h-5 w-1/4 bg-slate-700/60" />
                <Skeleton className="h-5 w-1/2 bg-slate-700/60" />
                <Skeleton className="h-10 w-40 bg-slate-700/60" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderEmptyState = (title, description) => (
    <Card className="border-slate-700/70 bg-slate-800/70 shadow-xl shadow-slate-950/20">
      <CardContent className="flex flex-col items-center justify-center px-6 py-14 text-center">
        <Calendar className="mb-4 h-12 w-12 text-slate-500" />
        <h3 className="text-xl font-semibold text-white">{title}</h3>
        <p className="mt-2 max-w-xl text-sm text-slate-400">{description}</p>
      </CardContent>
    </Card>
  );

  const renderAppointmentCard = (appointment) => (
    <Card
      key={appointment.id}
      className={cn(
        'cursor-pointer overflow-hidden rounded-[28px] border bg-slate-800/85 shadow-[0_24px_80px_-40px_rgba(15,23,42,1)] transition-colors hover:border-blue-400/40',
        appointment.isWaiting
          ? 'border-orange-400/50 ring-1 ring-orange-400/20'
          : 'border-slate-700/80'
      )}
      data-testid={`appointment-${appointment.id}`}
      role="button"
      tabIndex={0}
      onClick={() => openAppointmentDetails(appointment)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openAppointmentDetails(appointment);
        }
      }}
    >
      <CardContent className="p-5 md:p-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-stretch">
          <div className="flex w-full shrink-0 flex-row rounded-[26px] border border-amber-200/20 bg-[#372f37] px-5 py-4 text-amber-300 md:w-32 md:flex-col md:items-center md:justify-between">
            <div className="text-lg font-semibold">{appointment.time}</div>
            <div className="text-center">
              <div className="text-5xl font-black leading-none">{format(appointment.date, 'dd')}</div>
              <div className="mt-2 text-2xl font-bold uppercase tracking-wide">
                {format(appointment.date, 'MMM', { locale })}
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14 border border-slate-600">
                    <AvatarImage src={appointment.patient.image} />
                    <AvatarFallback className="bg-blue-200 text-base font-bold text-slate-800">
                      {appointment.patient.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0">
                    <h3 className="truncate text-2xl font-bold text-white">{appointment.patient.name}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {appointment.isWaiting && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-800 dark:border-orange-400/30 dark:bg-orange-400/15 dark:text-orange-100">
                          <Clock3 className="h-4 w-4" />
                          {lang === 'ar' ? 'موعد انتظار' : 'Waiting Appointment'}
                        </span>
                      )}
                      <span className="rounded-full border border-cyan-300 bg-cyan-50 px-3 py-1 text-sm font-semibold text-cyan-800 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-100">
                        {appointment.appointmentModeLabel}
                      </span>
                    </div>
                    <p className="mt-1 text-lg text-slate-300">
                      {appointment.type}
                      {appointment.clinic ? ` • ${appointment.clinic}` : ''}
                    </p>
                  </div>
                </div>

                <div className="mt-5 border-t border-slate-700/80 pt-5 text-slate-300">
                  <div className="flex flex-wrap items-center gap-4 text-lg">
                    <span className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-slate-400" />
                      {formatAppointmentDate(appointment.date)}
                    </span>
                    {appointment.patient.age != null && (
                      <span>
                        {appointment.patient.age} {lang === 'ar' ? 'سنة' : 'years'}
                      </span>
                    )}
                    {appointment.patient.gender && <span>{normalizeGender(appointment.patient.gender, lang)}</span>}
                  </div>

                  {appointment.notes && (
                    <p className="mt-4 rounded-2xl bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                      {appointment.notes}
                    </p>
                  )}

                  {hasQueueInsights(appointment) && (
                    <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-300">
                      {appointment.showQueueNumberToPatient && appointment.queueDisplayLabel && (
                        <span className="rounded-full border border-orange-400/20 bg-orange-400/10 px-3 py-1">
                          {lang === 'ar' ? 'الدور' : 'Queue'}: {appointment.queueDisplayLabel}
                        </span>
                      )}
                      {appointment.showRemainingTurnsToPatient &&
                        appointment.remainingTurnsCount != null && (
                          <span className="rounded-full border border-blue-400/20 bg-blue-400/10 px-3 py-1">
                            {lang === 'ar' ? 'الأدوار المتبقية' : 'Remaining Turns'}:{' '}
                            {appointment.remainingTurnsCount}
                          </span>
                        )}
                      {appointment.showEstimatedTurnTimeToPatient &&
                        appointment.estimatedWaitTime != null && (
                          <span className="rounded-full border border-teal-400/20 bg-teal-400/10 px-3 py-1">
                            {lang === 'ar' ? 'الوقت المتوقع' : 'Estimated Time'}:{' '}
                            {appointment.estimatedWaitTime} {lang === 'ar' ? 'دقيقة' : 'min'}
                          </span>
                        )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-start gap-4 lg:items-end">
                {getStatusBadge(appointment.statusId, appointment.status)}

                {(() => {
                  const nextStatus = getNextStatus(appointment.status);

                  return (nextStatus || canCancelAppointment(appointment)) && (
                    <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 rounded-full border border-slate-600 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-white"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <MoreVertical className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="min-w-[180px] border-slate-700 bg-slate-900 text-slate-100"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {nextStatus && (
                          <DropdownMenuItem
                            className="cursor-pointer focus:bg-slate-800 focus:text-white"
                            onClick={() => handleAdvanceAppointmentStatus(appointment)}
                          >
                            <CheckCircle className="h-4 w-4" />
                            {getStatusLabel(nextStatus, lang)}
                          </DropdownMenuItem>
                        )}
                      {canCancelAppointment(appointment) && (
                        <DropdownMenuItem
                          className="cursor-pointer text-rose-300 focus:bg-rose-500/15 focus:text-rose-200"
                          onClick={() => handleCancelAppointment(appointment)}
                        >
                          <XCircle className="h-4 w-4" />
                          {lang === 'ar' ? 'إلغاء الموعد' : 'Cancel Appointment'}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })()}

              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!isAuthenticated) return null;

  return (
    <div className="doctor-appointments-theme theme-shell min-h-screen" data-testid="doctor-appointments">
      <section className="theme-hero">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 lg:px-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/doctor/dashboard')}
                className="theme-outline-button mt-1 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <div>
                <h1 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white md:text-5xl">
                  {lang === 'ar' ? 'مواعيد الطبيب' : 'Doctor Appointments'}
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={fetchAppointments}
                className="theme-outline-button rounded-2xl"
              >
                <RefreshCw className="h-4 w-4" />
                {lang === 'ar' ? 'تحديث' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 lg:px-10">
        <div className="theme-panel rounded-[32px] p-4 md:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative w-full xl:max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder={lang === 'ar' ? 'ابحث باسم المريض أو المرجع...' : 'Search by patient or reference...'}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-12 rounded-2xl border-slate-300 bg-white pl-11 text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900/70 dark:text-white"
                data-testid="search-appointments"
              />
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 dark:border-slate-600 dark:bg-slate-900/70">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="h-12 border-0 bg-transparent px-0 text-slate-900 shadow-none focus-visible:ring-0 dark:text-white"
                />
              </div>

              <Button
                variant="outline"
                onClick={() => setSelectedDate('')}
                className="rounded-2xl border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <X className="h-4 w-4" />
                {lang === 'ar' ? 'إلغاء فلترة التاريخ' : 'Clear Date Filter'}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-300">
            <span className="rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1">
              {selectedDate
                ? lang === 'ar'
                  ? `المعروض: ${format(new Date(selectedDate), 'dd MMM yyyy', { locale })}`
                  : `Showing: ${format(new Date(selectedDate), 'dd MMM yyyy', { locale })}`
                : lang === 'ar'
                  ? 'المعروض: كل الأيام'
                  : 'Showing: all dates'}
            </span>
            <span className="rounded-full border border-slate-600 bg-slate-900/60 px-3 py-1">
              {lang === 'ar' ? `المجدولة: ${scheduledAppointments.length}` : `Scheduled: ${scheduledAppointments.length}`}
            </span>
            <span className="rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1">
              {lang === 'ar' ? `الانتظار: ${waitingAppointments.length}` : `Waiting: ${waitingAppointments.length}`}
            </span>
          </div>
        </div>

        <section className="mt-8">
          <div className="theme-panel rounded-[32px] p-3">
            <div className="flex flex-wrap gap-3">
              {doctorSettings.enableWaitingAppointments && (
                <button
                  type="button"
                  onClick={() => setActiveStatus('waiting_only')}
                  className={cn(
                    'rounded-2xl border px-5 py-3 text-base font-semibold transition-all duration-200',
                    activeStatus === 'waiting_only'
                      ? 'border-orange-500 bg-orange-500 text-[rgb(255,255,255)] shadow-lg shadow-orange-500/25 ring-2 ring-orange-200/80 dark:ring-orange-500/20'
                      : 'border-orange-300 bg-orange-50 text-orange-700 hover:border-orange-400 hover:bg-orange-100 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-100 dark:hover:bg-orange-500/20'
                  )}
                >
                  {lang === 'ar' ? 'مواعيد الانتظار' : 'Waiting Appointments'} ({waitingAppointments.length})
                </button>
              )}
              {visibleStatusFilters.map((filter) => {
                const isActive = activeStatus === filter.key;
                return (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => setActiveStatus(filter.key)}
                    className={cn(
                      'rounded-2xl border px-5 py-3 text-base font-semibold transition-all duration-200',
                      isActive
                        ? 'border-indigo-500 bg-indigo-500 text-[rgb(255,255,255)] shadow-lg shadow-indigo-500/25 ring-2 ring-indigo-200/80 dark:ring-indigo-500/20'
                        : 'border-slate-200 bg-slate-100 text-slate-700 hover:border-slate-300 hover:bg-slate-200 dark:border-slate-700/70 dark:bg-slate-700/70 dark:text-slate-200 dark:hover:bg-slate-700'
                    )}
                  >
                    {lang === 'ar' ? filter.ar : filter.en} ({statusCounts[filter.key] || 0})
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mt-8">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                {lang === 'ar' ? 'المواعيد المجدولة' : 'Scheduled Appointments'}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {lang === 'ar'
                  ? 'تبدأ الصفحة بمواعيد اليوم، وتنتقل تلقائيًا إلى مقبول أو قيد الانتظار بحسب إعداد الطبيب.'
                  : 'The page starts with today appointments and defaults to accepted or pending based on doctor settings.'}
              </p>
            </div>
          </div>

          {loading ? (
            renderLoadingCards()
          ) : filteredDisplayAppointments.length === 0 ? (
            renderEmptyState(
              lang === 'ar' ? 'لا توجد مواعيد مجدولة' : 'No scheduled appointments',
              lang === 'ar'
                ? 'لم يتم العثور على مواعيد مطابقة لخيارات البحث أو حالة الموعد أو التاريخ المحدد.'
                : 'No appointments match the current search, status, or date filters.'
            )
          ) : (
            <div className="space-y-5">
              {filteredDisplayAppointments.map((appointment) => renderAppointmentCard(appointment))}
            </div>
          )}
        </section>

        <section className={cn('mt-10', 'hidden')}>
          <div className="mb-5 flex items-center gap-3">
            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/10 p-3 text-orange-200">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {lang === 'ar' ? 'مواعيد الانتظار' : 'Waiting Appointments'}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {lang === 'ar'
                  ? 'قسم منفصل لمتابعة الدور والوقت المتوقع للحالات المنتظرة.'
                  : 'A separate section to follow queue order and estimated time for waiting cases.'}
              </p>
            </div>
          </div>

          {loading ? (
            renderLoadingCards()
          ) : waitingAppointments.length === 0 ? (
            renderEmptyState(
              lang === 'ar' ? 'لا توجد مواعيد انتظار' : 'No waiting appointments',
              lang === 'ar'
                ? 'لا توجد حالات انتظار مطابقة للفلاتر الحالية.'
                : 'There are no waiting appointments matching the current filters.'
            )
          ) : (
            <div className="space-y-5">
              {waitingAppointments.map((appointment) => renderAppointmentCard(appointment))}
            </div>
          )}
        </section>
      </div>

    </div>
  );
};

export default DoctorAppointments;
